/**
 * Generated by generate-assessment-schemas.ts from assessment_schemas.yaml
 * Do not edit manually — regenerate with: npm run codegen
 */
import { z } from "zod";

export const CARS2Category = z
  .object({
    Name: z.string().describe("Category label as printed, e.g. '1. Relating to People'"),
    Score: z
      .union([z.number(), z.null()])
      .describe("Clinician rating: 1, 1.5, 2, 2.5, 3, 3.5, or 4. Null if unscored.")
      .optional()
      .default(null),
  })
  .loose()
  .describe("A single scored item on the CARS-2 form (1 of 15).");
export type CARS2Category = z.infer<typeof CARS2Category>;

export const CARS2ParsedResult = z
  .object({
    "CARS-2 Type": z
      .union([z.string(), z.null()])
      .describe(
        "Form variant, e.g. 'CARS-2 (Childhood Autism Rating Scale, 2nd Edition-Standard Version)'. Optional because image parsing may fail to detect the variant; downstream enrichment surfaces this as missing rather than silently guessing ST vs HF.",
      )
      .optional()
      .default(null),
    "Assessment Date": z
      .union([z.string(), z.null()])
      .describe("Date of administration (ISO format string or free-text). Null if not recorded.")
      .optional()
      .default(null),
    Categories: z
      .array(CARS2Category)
      .min(1)
      .describe(
        "Scored items — expect 15 for a complete form. min_length=1 (not 15) is intentional: Gemini vision extraction may return partial results from blurry scans.",
      ),
    "Total Score": z
      .union([z.number().min(15).max(60), z.null()])
      .describe("Sum of 15 category scores (15-60). Null if not yet computed.")
      .optional()
      .default(null),
    "Selected Severity Group": z
      .union([z.string(), z.null()])
      .describe("Severity band printed/circled on the form. Null until extraction is updated to capture it.")
      .optional()
      .default(null),
  })
  .loose()
  .describe(
    "Layer 1 parsed result for CARS-2 (Standard or High-Functioning).\n\nContains only data that appears on the physical scoring form.\nExcluded (Layer 2): category descriptions, severity group reference table.",
  );
export type CARS2ParsedResult = z.infer<typeof CARS2ParsedResult>;

export const ADIRHeader = z
  .object({
    "Respondent Name": z
      .union([z.string(), z.null()])
      .describe("Name of the person interviewed.")
      .optional()
      .default(null),
    "Relation to Subject": z
      .union([z.string(), z.null()])
      .describe("Relationship of respondent to the subject.")
      .optional()
      .default(null),
    "Clinician Name": z
      .union([z.string(), z.null()])
      .describe("Name of the administering clinician.")
      .optional()
      .default(null),
    "School/Clinic": z
      .union([z.string(), z.null()])
      .describe("School or clinic where interview took place.")
      .optional()
      .default(null),
    "Date of Interview": z
      .union([z.string(), z.null()])
      .describe("Date of interview (ISO format string or free-text). Null if not recorded.")
      .optional()
      .default(null),
  })
  .partial()
  .loose()
  .describe("Interview metadata from the top of the ADI-R scoring form.");
export type ADIRHeader = z.infer<typeof ADIRHeader>;

export const ADIRScoreEntry = z
  .object({
    "Sub-category": z.string().describe("Sub-category label, e.g. 'A1', 'B (NV) Total'."),
    Score: z.union([z.number(), z.string()]).describe("Integer score, or empty string if not scored."),
  })
  .loose()
  .describe(
    "A single row in the ADI-R scoring table.\n\nRepresents either a sub-category score (e.g. A1, A2) or a category\ntotal (e.g. A Total, B (V) Total) as written on the physical form.\nCut-off values are NOT included here — they are constants added during\nLayer 2 enrichment from ADIR_CUTOFFS lookup tables.",
  );
export type ADIRScoreEntry = z.infer<typeof ADIRScoreEntry>;

export const ADIRParsedResult = z
  .object({
    Header: ADIRHeader,
    "Current Behavior Algorithm": z
      .union([z.string(), z.null()])
      .describe(
        "Selected Current Behavior Algorithm age range, e.g. '4 Years, 0 Months to 9 Years, 11 Months'. Null if not marked.",
      )
      .optional()
      .default(null),
    "Diagnostic Algorithm": z
      .union([z.string(), z.null()])
      .describe("Selected Diagnostic Algorithm age range, e.g. '4 Years, 0 Months or Older'. Null if not marked.")
      .optional()
      .default(null),
    Scores: z
      .record(z.string(), z.array(ADIRScoreEntry))
      .describe("Scoring categories (A, B, C, D) each containing sub-category score entries as filled in on the form."),
  })
  .loose()
  .describe(
    'Layer 1 parsed result for ADI-R (Autism Diagnostic Interview – Revised).\n\nContains only data that appears on the physical Comprehensive Algorithm Form.\nExcluded (Layer 2): cut-off values (from ADIR_CUTOFFS lookup), "Cutoff Met"\ncomparisons, total_score_abc summary table, customRowAttributes highlighting.',
  );
export type ADIRParsedResult = z.infer<typeof ADIRParsedResult>;

export const ADIRToddlerHeader = z
  .object({
    "Date of ADI-R Administration": z.union([z.string(), z.null()]).optional().default(null),
    "Participant ID": z.union([z.string(), z.null()]).optional().default(null),
  })
  .partial()
  .loose()
  .describe("Header metadata from the ADI-R toddler algorithm form.");
export type ADIRToddlerHeader = z.infer<typeof ADIRToddlerHeader>;

export const ADIRToddlerRow = z
  .object({
    algorithm_name: z.string(),
    section_heading: z.string(),
    subheading: z.string(),
    value: z.union([z.string(), z.null()]).optional().default(null),
    code: z.union([z.string(), z.null()]).optional().default(null),
    score: z.union([z.string(), z.null()]).optional().default(null),
    row_layout: z.string().describe("Layout of the row on the form. Valid values are 'value' and 'code_score'"),
  })
  .loose()
  .describe("Single extracted row from ADI-R toddler algorithm form.");
export type ADIRToddlerRow = z.infer<typeof ADIRToddlerRow>;

export const ADIRToddlerParsedResult = z
  .object({ Header: ADIRToddlerHeader, Rows: z.array(ADIRToddlerRow).optional() })
  .loose()
  .describe("Layer 1 parsed result for ADI-R Toddler.");
export type ADIRToddlerParsedResult = z.infer<typeof ADIRToddlerParsedResult>;

export const RIAS2GraphPoint = z
  .object({
    "Graph Name": z.string(),
    Series: z.union([z.string(), z.null()]).optional().default(null),
    Label: z.string(),
    Value: z.union([z.string(), z.null()]).optional().default(null),
  })
  .loose()
  .describe(
    'A data point from a profile or battery chart.\n\nSections "RIAS-2 Profiles" and "RIAS-2 Total Battery Profiles"\ncontain items of this shape.',
  );
export type RIAS2GraphPoint = z.infer<typeof RIAS2GraphPoint>;

export const RIAS2IndexTScore = z
  .object({
    headers: z.array(z.string()),
    data: z.array(z.record(z.string(), z.unknown())),
    customRowAttributeField: z.union([z.string(), z.null()]).optional().default(null),
  })
  .partial()
  .loose()
  .describe("Summary index T-score table (the primary output table).");
export type RIAS2IndexTScore = z.infer<typeof RIAS2IndexTScore>;

export const RIAS2SectionTable = z
  .object({
    "Table Name": z.string(),
    "Column Headers": z.array(z.string()).optional(),
    Rows: z.array(z.record(z.string(), z.string())).optional(),
  })
  .loose()
  .describe(
    'A score table within a RIAS-2 section.\n\nSections like "Subtest Scores/Index Summary", "RIAS-2 Extended Score\nSummary Table", and "RIAS-2 Discrepancy Score Summary Table" contain\nitems of this shape.',
  );
export type RIAS2SectionTable = z.infer<typeof RIAS2SectionTable>;

export const RIAS2Sections = z
  .object({
    "Interpretive Report": z.array(z.record(z.string(), z.string())),
    "Test Info": z.array(z.record(z.string(), z.string())),
    "Base information": z.array(z.record(z.string(), z.string())),
    "Additional Information (Optional)": z.array(z.record(z.string(), z.string())),
    "Score summary": z.array(z.record(z.string(), z.string())),
    "Subtest Scores/Index Summary": z.array(RIAS2SectionTable),
    "RIAS-2 Extended Score Summary Table": z.array(RIAS2SectionTable),
    "RIAS-2 Discrepancy Score Summary Table": z.array(RIAS2SectionTable),
    "RIAS-2 Profiles": z.array(RIAS2GraphPoint),
    "RIAS-2 Total Battery Profiles": z.array(RIAS2GraphPoint),
    "Other Notes": z.array(z.record(z.string(), z.string())),
  })
  .partial()
  .loose()
  .describe(
    'Typed container for RIAS-2 section data.\n\nKnown section keys are typed fields with aliases matching the JSON keys.\nDynamic narrative heading keys (e.g. "Background Information",\n"Composite Norm-Referenced Interpretations") are captured via\nextra="allow".',
  );
export type RIAS2Sections = z.infer<typeof RIAS2Sections>;

export const RIAS2ParsedResult = z
  .object({
    sections: RIAS2Sections,
    index_tscore: RIAS2IndexTScore.optional(),
    headings_found: z.array(z.string()).optional(),
  })
  .loose()
  .describe("Layer 1 parsed result for RIAS-2.");
export type RIAS2ParsedResult = z.infer<typeof RIAS2ParsedResult>;

export const ItemScore = z
  .object({
    SessionId: z.number().min(1).max(50).describe("References AssessmentSession.Id."),
    Score: z.number().min(0).describe("Cell value as scored. Always >= 0; <= containing task's Max Score."),
    Assessed: z
      .boolean()
      .describe(
        "True if the cell was scored (incl. score=0); False to record an explicitly-not-assessed cell. Absence from Scores[] also means not assessed.",
      )
      .optional(),
  })
  .loose()
  .describe("One scored cell at the intersection of a task and a session.");
export type ItemScore = z.infer<typeof ItemScore>;

export const ABLLSTask = z
  .object({
    Code: z.string().describe("Task code as printed on the form, e.g. 'A1', 'B12'."),
    Name: z.string().describe("Task description text."),
    "Max Score": z.number().min(1).describe("Maximum attainable score for this task."),
    Scores: z
      .array(ItemScore)
      .min(0)
      .describe(
        "Per-session scores. Absence of a SessionId from this list means the task was not assessed in that session.",
      ),
  })
  .loose()
  .describe("A single task / item code on the form (e.g. 'A1').");
export type ABLLSTask = z.infer<typeof ABLLSTask>;

export const ABLLSSkillArea = z
  .object({
    Name: z.string().describe("Skill area / sub-domain name."),
    "Max Score": z.number().min(1).describe("Sum of contained Tasks[].Max Score."),
    Tasks: z.array(ABLLSTask).min(1).describe("Tasks within this skill area."),
  })
  .loose()
  .describe("A grouping of related tasks (e.g. 'Cooperation and Reinforcer Effectiveness').");
export type ABLLSSkillArea = z.infer<typeof ABLLSSkillArea>;

export const AssessmentSession = z
  .object({
    Id: z
      .number()
      .min(1)
      .max(50)
      .describe(
        "Session's true original administration number (matches ItemScore.SessionId); may exceed the retained session count once earlier administrations are dropped.",
      ),
    Date: z.string().describe("Administration date — MM/DD/YYYY, YYYY-MM-DD, or MM-DD-YYYY.").optional(),
    Assessor: z
      .union([z.string(), z.null()])
      .describe("Assessor name as written on the form. Empty / None if blank.")
      .optional()
      .default(null),
    Color: z.string().describe("Color used to fill this session's cells, '#RRGGBB' hex.").optional(),
  })
  .loose()
  .describe("One administration column; up to 8 kept, each with its original number.");
export type AssessmentSession = z.infer<typeof AssessmentSession>;

export const ABLLSParsedResult = z
  .object({
    "Assessment Type": z.string().describe("Discriminator literal: 'ABLLS'."),
    "Max Score": z.number().min(1).describe("Sum of Skill Areas[].Max Score."),
    Sessions: z.array(AssessmentSession).min(1).describe("1-8 assessment session columns."),
    "Skill Areas": z.array(ABLLSSkillArea).min(1).describe("Skill area groupings, each containing 1+ tasks."),
  })
  .loose()
  .describe(
    "Layer 1 parsed result for ABLLS-R.\n\nHierarchical structure: Sessions (up to 8) × Skill Areas × Tasks × Scores.\nIdentical structure to AFLS — only ``Assessment Type`` literal differs.",
  );
export type ABLLSParsedResult = z.infer<typeof ABLLSParsedResult>;

export const AFLSParsedResult = z
  .object({
    "Assessment Type": z.string().describe("Discriminator literal: 'AFLS'."),
    "Max Score": z.number().min(1).describe("Sum of Skill Areas[].Max Score."),
    Sessions: z.array(AssessmentSession).min(1).describe("1-8 assessment session columns."),
    "Skill Areas": z.array(ABLLSSkillArea).min(1).describe("Skill area groupings, each containing 1+ tasks."),
  })
  .loose()
  .describe(
    "Layer 1 parsed result for AFLS (Assessment of Functional Living Skills).\n\nStructurally identical to ABLLSParsedResult; only ``Assessment Type``\ndiffers. Reuses ABLLS's SkillArea / Task / ItemScore / Session models.",
  );
export type AFLSParsedResult = z.infer<typeof AFLSParsedResult>;

export const EarliPointParsedResult = z
  .object({
    "Patient Age at Evaluation": z
      .union([z.string(), z.null()])
      .describe("Age at evaluation, as printed, e.g. '1 year 7 months'.")
      .optional()
      .default(null),
    "Session Quality": z
      .union([z.string(), z.null()])
      .describe("Session quality flag as printed, e.g. 'PASS'.")
      .optional()
      .default(null),
    "Session Date": z
      .union([z.string(), z.null()])
      .describe(
        "Date/time the EarliPoint session was administered, as printed. Used to sanity-check against the report writer, not as the source of truth for patient metadata (already known from upload context).",
      )
      .optional()
      .default(null),
    "Prescribing Clinician": z
      .union([z.string(), z.null()])
      .describe("Name of the prescribing clinician, as printed.")
      .optional()
      .default(null),
    "Device Operator": z
      .union([z.string(), z.null()])
      .describe(
        "Name of the device operator, as printed. A distinct printed field from Prescribing Clinician — sometimes the same person (with different name formatting), sometimes a different person entirely (e.g. a technician). Extract both; do not coalesce.",
      )
      .optional()
      .default(null),
    "Diagnostic Index Score": z
      .union([z.number(), z.null()])
      .describe("Overall EarliPoint Diagnostic Index Score.")
      .optional()
      .default(null),
    "Diagnostic Classification": z
      .union([z.string(), z.null()])
      .describe(
        "Normalized to 'Consistent with Autism', 'Not Consistent with Autism', or 'Borderline'. Prefer the explicit 'Diagnostic Result:' value when printed (rev K); fall back to the classification heading text when absent (rev I).",
      )
      .optional()
      .default(null),
    "Diagnostic Narrative": z
      .union([z.string(), z.null()])
      .describe(
        "Descriptive sentence printed under the classification heading, verbatim (e.g. 'did not clearly indicate or rule out autism'). Captured unvalidated — this is the only additional context available for Borderline outcomes, which don't get a percentage.",
      )
      .optional()
      .default(null),
    "Additional Assessment Recommended": z
      .union([z.boolean(), z.null()])
      .describe(
        "True when the report explicitly states additional assessment is recommended (seen on Borderline outcomes). Null, not False, when the report doesn't address this at all.",
      )
      .optional()
      .default(null),
    "False Negative Statement": z
      .union([z.string(), z.null()])
      .describe(
        "Verbatim false-negative-risk disclaimer sentence, when present (observed on Not-Autism outcomes only, e.g. 'In testing centers where many referred patients have autism, there is a chance of false negatives...'). Null when the report has no such statement.",
      )
      .optional()
      .default(null),
    "Social Disability Index Score": z.union([z.number(), z.null()]).optional().default(null),
    "Social Disability Severity": z
      .union([z.string(), z.null()])
      .describe("Normalized to 'High/Moderate/Low/Minimal Concern'.")
      .optional()
      .default(null),
    "Estimated ADOS-2 Total Score": z
      .union([z.number(), z.null()])
      .describe("The 'Corresponds to an ADOS-2 Total Score of X' figure from the Social Disability box on page 2.")
      .optional()
      .default(null),
    "Estimated ADOS-2 Symptom Level": z
      .union([z.string(), z.null()])
      .describe(
        "Normalized to 'High', 'Moderate', 'Low', or 'Minimal-to-No Evidence'. This is printed as plain prose on the detail page (e.g. 'this score correlates to minimal-to-no evidence of autism symptoms on the ADOS-2') — read directly, not inferred from the severity label or a chart position.",
      )
      .optional()
      .default(null),
    "Language Comprehension Index Score": z
      .union([z.number(), z.null()])
      .describe("Same index as 'Verbal Ability' in rev I report templates.")
      .optional()
      .default(null),
    "Language Comprehension Age Equivalent": z
      .union([z.string(), z.null()])
      .describe("Raw as printed, e.g. '12 months' or '2 years 9 months'.")
      .optional()
      .default(null),
    "Language Comprehension Age Equivalent CI": z
      .union([z.string(), z.null()])
      .describe(
        "95% confidence interval range, normalized to 'X to Y months' — the source PDF typically prints a hyphen/en dash/em dash between the numbers instead of the word 'to'; extraction always rewrites it, e.g. '11 to 15 months'.",
      )
      .optional()
      .default(null),
    "Language Comprehension Comparison Sentence": z
      .union([z.string(), z.null()])
      .describe(
        "Verbatim sentence comparing the age equivalent to chronological age, e.g. 'This is approximately the same as her chronological age of 4 years 2 months, indicating age-expected language ability.' The source page has a near-duplicate caption sentence next to the ruler graphic that omits the age number and starts with the patient's name instead — extraction prefers the 'This is...' paragraph sentence with the explicit age figure.",
      )
      .optional()
      .default(null),
    "Nonverbal Learning Index Score": z.union([z.number(), z.null()]).optional().default(null),
    "Nonverbal Learning Age Equivalent": z
      .union([z.string(), z.null()])
      .describe("Raw as printed, e.g. '23 months' or '3 years 0 months'.")
      .optional()
      .default(null),
    "Nonverbal Learning Age Equivalent CI": z
      .union([z.string(), z.null()])
      .describe(
        "95% confidence interval range, normalized to 'X to Y months' — the source PDF typically prints a hyphen/en dash/em dash between the numbers instead of the word 'to'; extraction always rewrites it, e.g. '18 to 20 months'.",
      )
      .optional()
      .default(null),
    "Nonverbal Learning Comparison Sentence": z
      .union([z.string(), z.null()])
      .describe(
        "Verbatim sentence comparing the age equivalent to chronological age. Same disambiguation as Language Comprehension Comparison Sentence — prefer the 'This is...' paragraph sentence with the explicit age figure over the ruler-graphic caption sentence.",
      )
      .optional()
      .default(null),
  })
  .partial()
  .loose()
  .describe(
    'Layer 1 parsed result for EarliPoint.\n\nReport templates vary across revisions (e.g. "Verbal Ability" was renamed to\n"Language Comprehension" between rev I and rev K) — extraction normalizes\nonto this canonical field set regardless of which wording the source PDF uses.',
  );
export type EarliPointParsedResult = z.infer<typeof EarliPointParsedResult>;

export const KBIT2ScoreComparison = z
  .object({
    "Comparison Type": z
      .string()
      .describe("'Standard Score' (Verbal vs. Nonverbal) or 'Scaled Score' (Verbal Knowledge vs. Riddles)."),
    "Measure A Label": z.string(),
    "Measure A Score": z.union([z.number(), z.null()]).optional().default(null),
    "Measure B Label": z.string(),
    "Measure B Score": z.union([z.number(), z.null()]).optional().default(null),
    Difference: z.union([z.number(), z.null()]).optional().default(null),
    "Significance of Difference": z
      .union([z.string(), z.null()])
      .describe("e.g. '< .01', '< .05', or 'NS' (not significant).")
      .optional()
      .default(null),
    "Frequency of Occurrence": z
      .union([z.string(), z.null()])
      .describe("e.g. '<= 10%'. Printed as '-' when significance is 'NS'.")
      .optional()
      .default(null),
  })
  .loose()
  .describe("A row of the Score Comparisons table (two fixed comparisons per report).");
export type KBIT2ScoreComparison = z.infer<typeof KBIT2ScoreComparison>;

export const KBIT2StandardScoreRow = z
  .object({
    Composite: z.string().describe("Row label as printed, e.g. 'Verbal', 'Nonverbal', 'IQ Composite'."),
    "Standard Score": z.union([z.number(), z.null()]).optional().default(null),
    "Percentile Rank": z.union([z.string(), z.null()]).describe("e.g. '88'.").optional().default(null),
    "Percentile Rank Confidence Interval": z
      .union([z.string(), z.null()])
      .describe(
        "Confidence interval for the percentile rank, printed in parentheses next to it, e.g. '79–93' from '88 (79–93)'.",
      )
      .optional()
      .default(null),
    "Confidence Interval": z
      .union([z.string(), z.null()])
      .describe("Standard-score confidence interval range, e.g. '112–122'.")
      .optional()
      .default(null),
    "Descriptive Category": z
      .union([z.string(), z.null()])
      .describe("e.g. 'Above average', 'Average'.")
      .optional()
      .default(null),
  })
  .loose()
  .describe("A row of the Standard Score Summary table (Verbal/Nonverbal/IQ Composite).");
export type KBIT2StandardScoreRow = z.infer<typeof KBIT2StandardScoreRow>;

export const KBIT2SubtestScoreRow = z
  .object({
    Subtest: z.string().describe("Row label as printed, e.g. 'Verbal Knowledge', 'Riddles', 'Matrices'."),
    "Raw Score": z.union([z.number(), z.null()]).optional().default(null),
    "Scaled Score": z
      .union([z.number(), z.null()])
      .describe("Null for Matrices — QGlobal reports print this cell blank for that subtest.")
      .optional()
      .default(null),
    "Age Equivalent": z
      .union([z.string(), z.null()])
      .describe(
        "Years:months as printed, e.g. '13:3'. May carry a floor prefix, e.g. '<4:0'. Keep as a string, not numeric.",
      )
      .optional()
      .default(null),
  })
  .loose()
  .describe("A row of the Subtest Score Summary table (Verbal Knowledge/Riddles/Matrices).");
export type KBIT2SubtestScoreRow = z.infer<typeof KBIT2SubtestScoreRow>;

export const KBIT2ParsedResult = z
  .object({
    "Date of Testing": z
      .union([z.string(), z.null()])
      .describe("From Examinee Information. ISO format (YYYY-MM-DD) if determinable.")
      .optional()
      .default(null),
    "Age at Testing": z
      .union([z.string(), z.null()])
      .describe("From Examinee Information, e.g. '9 years 5 months'.")
      .optional()
      .default(null),
    "Examiner Name": z
      .union([z.string(), z.null()])
      .describe("From Examinee Information, e.g. 'DeQuinzio, Jaime'.")
      .optional()
      .default(null),
    "Standard Score Summary": z
      .array(KBIT2StandardScoreRow)
      .min(1)
      .describe("Expect 3 rows (Verbal, Nonverbal, IQ Composite)."),
    "Subtest Score Summary": z
      .array(KBIT2SubtestScoreRow)
      .describe("Expect 3 rows (Verbal Knowledge, Riddles, Matrices).")
      .optional(),
    "Score Comparisons": z
      .array(KBIT2ScoreComparison)
      .describe("Expect 2 rows (Standard Score comparison, Scaled Score comparison).")
      .optional(),
  })
  .loose()
  .describe(
    "Layer 1 parsed result for KBIT-2.\n\nContains only data that appears on the QGlobal-generated score report.",
  );
export type KBIT2ParsedResult = z.infer<typeof KBIT2ParsedResult>;

export const BDI3DomainAdministered = z
  .object({
    Domain: z.string(),
    Examiner: z.union([z.string(), z.null()]).optional().default(null),
    "Date of Testing": z.union([z.string(), z.null()]).optional().default(null),
  })
  .loose()
  .describe(
    'A row from the Page 1 "Domain(s) Administered" table.\n\nOne row per domain administered; examiner/date may differ per domain.',
  );
export type BDI3DomainAdministered = z.infer<typeof BDI3DomainAdministered>;

export const BDI3DomainScore = z
  .object({
    Domain: z.string(),
    "Raw Score": z.union([z.number(), z.null()]).optional().default(null),
    "Standard Deviation": z.union([z.number(), z.null()]).optional().default(null),
    "Cut Score": z.union([z.number(), z.null()]).optional().default(null),
    "Pass/Refer": z.union([z.string(), z.null()]).optional().default(null),
  })
  .loose()
  .describe('A row from the Page 2 "Table of Scores" — one per administered domain.');
export type BDI3DomainScore = z.infer<typeof BDI3DomainScore>;

export const BDI3ItemScore = z
  .object({
    DOMAIN: z.string(),
    SKILL: z.string().describe("Free-text item description, drawn from a bank of up to 415 possible items."),
    MASTERY: z.string().describe('One of "MASTERED", "EMERGING", "FUTURE LEARNING OBJECTIVE", verbatim and all caps.'),
  })
  .loose()
  .describe(
    'A row from the Page 4 "Item Level Scores" table.\n\nWhich items appear (and how many) is not fixed — it depends on the\nchild\'s age/ability (adaptive basal/ceiling testing) — so this is a\nvariable-length list of free-text skill + mastery pairs, grouped by\ndomain, not a fixed catalog of named fields.',
  );
export type BDI3ItemScore = z.infer<typeof BDI3ItemScore>;

export const BDI3TotalScreeningScore = z
  .object({
    "Raw Score": z.union([z.number(), z.null()]).optional().default(null),
    "Standard Deviation": z.union([z.number(), z.null()]).optional().default(null),
    "Cut Score": z.union([z.number(), z.null()]).optional().default(null),
    "Pass/Refer": z.union([z.string(), z.null()]).optional().default(null),
    "Age Equivalent": z
      .union([z.string(), z.null()])
      .describe("e.g. '15 Months'. Applies to the total, not to individual domains.")
      .optional()
      .default(null),
  })
  .partial()
  .loose()
  .describe(
    'The "Total Screening Score" row + Age Equivalent (Page 2).\n\nOnly present on reports where more than one domain was administered —\nthe total is not meaningful for a single-domain screening. Null on\nsingle-domain reports.',
  );
export type BDI3TotalScreeningScore = z.infer<typeof BDI3TotalScreeningScore>;

export const BDI3ParsedResult = z
  .object({
    Name: z.union([z.string(), z.null()]).optional().default(null),
    "Date of Testing": z.union([z.string(), z.null()]).optional().default(null),
    "Chronological Age": z
      .union([z.string(), z.null()])
      .describe("e.g. '4 years, 1 month, 27 days'.")
      .optional()
      .default(null),
    Gender: z.union([z.string(), z.null()]).optional().default(null),
    Location: z.union([z.string(), z.null()]).optional().default(null),
    "Date of Birth": z.union([z.string(), z.null()]).optional().default(null),
    Label: z.union([z.string(), z.null()]).optional().default(null),
    "Domain(s) Administered": z.array(BDI3DomainAdministered),
    "Table of Scores": z.array(BDI3DomainScore),
    "Total Screening Score": z.union([BDI3TotalScreeningScore, z.null()]).optional().default(null),
    "Item Level Scores": z.array(BDI3ItemScore),
  })
  .partial()
  .loose()
  .describe(
    "Layer 1 parsed result for the BDI-3 Screening Test score report.\n\nA report may cover just 1 domain or all 5 (Adaptive, Social-Emotional,\nCommunication, Motor, Cognitive) depending on what was administered.",
  );
export type BDI3ParsedResult = z.infer<typeof BDI3ParsedResult>;

export const BHISubcategory = z
  .object({
    Name: z.string().describe("Subcategory label as printed, e.g. 'Play / Leisure'."),
    "Initial Assessment Score": z
      .union([z.number().min(1).max(4), z.null()])
      .describe("Score checked in the 'INT. AST.' column. Null if unscored.")
      .optional()
      .default(null),
    "Assessment Today Score": z
      .union([z.number().min(1).max(4), z.null()])
      .describe("Score checked in the 'AST. TODAY' column. Null if unscored.")
      .optional()
      .default(null),
  })
  .loose()
  .describe(
    'A single scored item on the BHI form (1 of 15).\n\nThe form scores each subcategory twice: once at intake ("Initial\nAssessment") and again at the current visit ("Assessment Today") —\nprinted as the "INT. AST." and "AST. TODAY" columns.',
  );
export type BHISubcategory = z.infer<typeof BHISubcategory>;

export const BHIParsedResult = z
  .object({
    "Child / Client": z
      .union([z.string(), z.null()])
      .describe("Learner name as written on the form. Null if not recorded.")
      .optional()
      .default(null),
    Scorer: z
      .union([z.string(), z.null()])
      .describe("Clinician who scored the form. Null if not recorded.")
      .optional()
      .default(null),
    "Initial Assessment Date": z
      .union([z.string(), z.null()])
      .describe("Date of the initial assessment (ISO or free-text). Null if not recorded.")
      .optional()
      .default(null),
    "Today's Date": z
      .union([z.string(), z.null()])
      .describe("Date this scoring was recorded (ISO or free-text). Null if not recorded.")
      .optional()
      .default(null),
    Subcategories: z
      .array(BHISubcategory)
      .describe(
        "Scored items — expect 15 for a complete form. Fewer (including zero) is intentional: Gemini vision extraction may return partial results from blurry scans. Enrichment records the extracted count so a partial composite is never presented as complete.",
      ),
  })
  .loose()
  .describe(
    "Layer 1 parsed result for BHI (Behavioral Health Index).\n\nContains only data that appears on the physical scoring form. Excluded\n(Layer 2): per-subcategory descriptions, total score, category grouping.",
  );
export type BHIParsedResult = z.infer<typeof BHIParsedResult>;
